import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  IconButton,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

const ChatDetailsModal = ({ open, onClose, chatDetails }) => {
  const [groupedChats, setGroupedChats] = useState({});

  useEffect(() => {
    if (chatDetails?.length) {
      // Group chats by date
      const grouped = chatDetails.reduce((acc, chat) => {
        const date = format(new Date(chat.createdAt), "yyyy-MM-dd");
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(chat);
        return acc;
      }, {});

      setGroupedChats(grouped);
    }
  }, [chatDetails]);

  const getInitials = (name) => {
    if (!name || name === "—") return "?";
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getFeedbackIcon = (feedback) => {
    switch (feedback) {
      case "thumbs_up":
        return <ThumbUpIcon sx={{ color: "success.main", fontSize: 16 }} />;
      case "thumbs_down":
        return <ThumbDownIcon sx={{ color: "error.main", fontSize: 16 }} />;
      default:
        return <span style={{ color: "#666" }}>—</span>;
    }
  };

  const renderMessageWithReferences = (text) => {
    if (!text) return null;

    return (
      <ReactMarkdown
        components={{
          p: ({ node, children, ...props }) => (
            <div style={{ marginBottom: "8px", lineHeight: "1.6" }} {...props}>
              {children}
            </div>
          ),
          strong: ({ node, ...props }) => (
            <strong style={{ fontWeight: 600, color: "#000" }} {...props} />
          ),
        }}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
      >
        {text}
      </ReactMarkdown>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", pr: 2 }}
      >
        <Typography variant="h6" component="span">
          💬 Chat Details
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#f9f9f9" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Summary Stats */}
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              📊 Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Total Chats
                </Typography>
                <Typography variant="h5">{chatDetails?.length || 0}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Positive Feedback
                </Typography>
                <Typography variant="h5" color="success.main">
                  {chatDetails?.filter((chat) => chat.feedback === "thumbs_up")
                    .length || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Negative Feedback
                </Typography>
                <Typography variant="h5" color="error.main">
                  {chatDetails?.filter(
                    (chat) => chat.feedback === "thumbs_down"
                  ).length || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Unique Users
                </Typography>
                <Typography variant="h5">
                  {new Set(
                    chatDetails
                      ?.map((chat) => chat.session?.user?.id)
                      .filter(Boolean)
                  ).size || 0}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* All Conversations */}
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, maxHeight: 500, overflow: "auto" }}>
              <Typography variant="h6" gutterBottom>
                💬 All Conversations
              </Typography>
              {Object.entries(groupedChats)
                .sort(([a], [b]) => new Date(b) - new Date(a))
                .map(([date, chats]) => (
                  <Box key={date} sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, color: "primary.main" }}
                    >
                      {format(new Date(date), "EEEE, MMMM dd, yyyy")} (
                      {chats.length} chats)
                    </Typography>
                    {chats.map((chat, index) => (
                      <Paper
                        key={chat.id || index}
                        elevation={1}
                        sx={{ p: 2, mb: 2, borderRadius: 2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "primary.main",
                              width: 32,
                              height: 32,
                            }}
                          >
                            {getInitials(chat.session?.user?.name)}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Typography variant="subtitle2">
                                {chat.session?.user?.name || "Anonymous"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {format(new Date(chat.createdAt), "HH:mm")}
                              </Typography>
                              {getFeedbackIcon(chat.feedback)}
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Q:</strong> {chat.question}
                            </Typography>
                            <Typography variant="body2">
                              <strong>A:</strong>{" "}
                              {chat?.completeResponse?.message
                                ? renderMessageWithReferences(
                                    chat.completeResponse.message
                                  )
                                : chat.answer || "No answer yet"}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ))}
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatDetailsModal;
